import { useState, useEffect } from "react";
import { Container, Typography } from "@material-ui/core";
import "./AssignmentListPanel.css";
import BookCard from "./BookCard";
import { connect } from "react-redux";
import BookResults from "./state/BookResults";
import { RootState } from "../../../state/configureStore";
import { Link, useHistory } from "react-router-dom";
import { StudentHomeScreenState } from "./state/StudentHomeScreenState";
import JoinClassroomModal from "../../Modals/JoinClassroomModal";
import Loading from "../../Loading/Loading";
import StandardModal from "../../Modals/StandardModal";
import ModalSize from "../../Modals/ModalSize";
import useModal from "../../Modals/UseModal";
import EmptyState from "../../EmptyState";

const BookResultToWidget = (bookResult: BookResults) => {
  return (
    <Link
      key={bookResult.assignmentId}
      className="bookGridItem"
      to={`/Book?assignmentid=${bookResult.assignmentId}&classid=${bookResult.classId}${
        bookResult.viewType === "paperback" ? "&project-paperback-student" : ""
      }`}
    >
      <BookCard
        coverImageUrl={bookResult.coverImageUrl}
        bookName={bookResult.bookName}
        className={bookResult.className}
        wordCount={bookResult.wordCount}
        uniqueWordCount={bookResult.uniqueWordCount}
        estimatedTime={bookResult.estimatedTime}
        isNew={bookResult.isNew}
        accuracy={bookResult.accuracy}
        wordsPerMinute={bookResult.wordsPerMinute}
        completion={bookResult.completion}
        studentFacingData={bookResult.studentFacingData}
        actualTime={bookResult.actualTime}
      />
    </Link>
  );
};

enum BookStatus {
  Started,
  NotStarted,
  Finished,
}
const isFinished = (book: BookResults) => book.completion > 0.99;
const isStarted = (book: BookResults) => book.completion > 0.01;
const getStatus = (book: BookResults): BookStatus =>
  (isFinished(book) && BookStatus.Finished) || (isStarted(book) && BookStatus.Started) || BookStatus.NotStarted;
const sortBooks = (book1: BookResults, book2: BookResults) => {
  if (!book1) {
    return 1;
  }
  if (!book2) {
    return -1;
  }
  const bookStatus1 = getStatus(book1);
  const bookStatus2 = getStatus(book2);

  if (bookStatus1 === bookStatus2) {
    return bookStatus1 === BookStatus.Started
      ? book1.completion - book2.completion
      : book1.bookName.localeCompare(book2.bookName);
  }
  return bookStatus1 - bookStatus2;
};

function AssignmentListPanel({
  state,
  currentUser,
}: { state: StudentHomeScreenState } & ReturnType<typeof mapStateToProps>) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    setOpen(Object.keys(state.studentBooks).length === 0 && !window.location.search.includes("joinClass"));
  }, [state.studentBooks]);

  // Force the checks to boolean to prevent a default to boolean error
  const bookResults = Object.values(state.studentBooks);
  const takeAssessmentModal = useModal(
    currentUser?.canTakeAssessment === true && currentUser?.needsAssessment === true,
  );
  const history = useHistory();

  const sortedBookResults = bookResults.sort(sortBooks);
  const newReadings = sortedBookResults.filter(result => !result.turnedIn).map(BookResultToWidget);
  const submittedReadings = sortedBookResults.filter(result => result.turnedIn).map(BookResultToWidget);
  return (
    <div className="StudentHomeScreenAssignmentList">
      <Container style={{ maxWidth: "var(--max-page-width)" }}>
        {bookResults.length === 0 ? (
          <EmptyState>No Assignments Found</EmptyState>
        ) : (
          <div>
            <Typography className="readingListLabel" variant="h5">
              New Assignments
            </Typography>
            <div
              className="bookGrid"
              style={{ display: "flex", flexFlow: "row wrap", justifyContent: "space-between" }}
            >
              {newReadings.length ? newReadings : <EmptyState>No New Assignments</EmptyState>}
            </div>
            <Typography className="readingListLabel" variant="h5">
              Submitted Assignments
            </Typography>
            <div
              className="bookGrid"
              style={{ display: "flex", flexFlow: "row wrap", justifyContent: "space-between" }}
            >
              {submittedReadings.length ? submittedReadings : <EmptyState>No Submitted Assignments</EmptyState>}
            </div>
            <StandardModal
              size={ModalSize.medium}
              open={takeAssessmentModal.open}
              onlyOneButton={false}
              title="Take Assessment"
              cancelClose={takeAssessmentModal.closeModal}
              onSubmit={() => {
                history.push("/Assessment");
              }}
            >
              Your teacher would like you to take the assessment. Please click "OK' to take the assessment.
            </StandardModal>
          </div>
        )}
      </Container>
      <JoinClassroomModal closeModal={() => setOpen(false)} open={open} />
    </div>
  );
}

const LoadingWrapper = (props: ReturnType<typeof mapStateToProps>) => {
  if (props.studentHomeState.isLoading) return <Loading />;
  return <AssignmentListPanel {...props} state={props.studentHomeState} />;
};

const mapStateToProps = (state: RootState) => ({
  currentUser: state.currentUserState.currentUser,
  studentHomeState: state.studentHomeScreenState,
});

export default connect(mapStateToProps)(LoadingWrapper);
